import Service from './Service';
import { Application, ApplicationDocument, DeviceInfo, LegalText, Location } from 'client-experience-toolbox';
import { DOTCheckResponse } from 'app/types';
import Config from 'Config';

type BackgroundCheckType = 'kyc' | 'kyb';

type GetAgreementsPayload = {
  type: string;
  documentType: string;
  language: string;
};

export type AgreementsMetadata = {
  type: string;
  documentType: string;
  version: string;
  s3VersionId: string;
  fileType: string;
  language: string;
  s3Key: string;
  checksumSha256: string;
};

export type GetAgreementsResponse = {
  documentType: string;
  presignedUrl: string;
  metadata: AgreementsMetadata;
};

export class DigitalOnboardingService extends Service {
  constructor() {
    super(Config.digitalOnboardingServiceUrl);
  }

  public async getApplicationById(id: string): Promise<Partial<Application>> {
    const response = await this.axios.get(`/application/${id}`);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch application data: ${response.statusText}`);
    }
    return response.data;
  }

  public async getCompanyByDOT(dot_number: string, type?: string): Promise<DOTCheckResponse> {
    const payload = type ? { dot_number, type } : { dot_number };
    const response = await this.axios.post<DOTCheckResponse>(`/dot-check`, payload);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch company data: ${response.statusText}`);
    }
    return response.data;
  }

  public async submitApplication(
    applicationId: string,
    body: {
      location: Location;
      deviceInfo: DeviceInfo;
    },
  ) {
    const response = await this.axios.patch(`/application/submit/${applicationId}`, body);
    if (response.status !== 200) {
      throw new Error(`Failed to submit application data: ${response.statusText}`);
    }
    return response;
  }

  public async submitBackgroundCheck(application_id: string, type: BackgroundCheckType) {
    const response = await this.axios.post('/create-kyb-kyc', {
      application_id,
      type,
    });
    if (response.status !== 200) {
      throw new Error(`Failed to submit background check: ${response.statusText}`);
    }
    return response.data;
  }

  public async patchApplication(applicationId: string, body: Application) {
    const response = await this.axios.patch(`/application/${applicationId}`, body);
    if (response.status !== 200) {
      throw new Error(`Failed to update application data: ${response.statusText}`);
    }
    return response.data as ApplicationDocument;
  }

  public async patchApplicationSecret(
    applicationId: string,
    body: { irsTaxIdOrEin?: string; ssnOrItin?: string; key?: string },
  ) {
    const response = await this.axios.patch(`/application/vault/${applicationId}`, body);
    if (response.status !== 200) {
      throw new Error(`Failed to update application vault: ${response.statusText}`);
    }
    return response.data as ApplicationDocument;
  }

  async getLegalText(type: string, language = 'en') {
    const formattedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    const res = await this.axios.get<LegalText[]>(`/get-legal-text?type=${formattedType}&language=${language}`);
    if (res.status !== 200) {
      throw new Error(`Failed to fetch legal text: ${res.statusText}`);
    }
    return res.data;
  }

  async getAgreements(body: GetAgreementsPayload): Promise<GetAgreementsResponse> {
    const normalizedParams = {
      ...body,
      type: body.type.toUpperCase(),
      language: body.language.split('-')[0].toLowerCase(),
    };

    const response = await this.axios.get('/agreements', {
      params: normalizedParams,
    });
    if (response.status !== 200) {
      throw new Error(`Failed to fetch agreements: ${response.statusText}`);
    }
    return response.data;
  }

  async getPresignedUrl(
    sourceType: string,
    documentType: string,
    filePath: string,
  ): Promise<{ presignedUrl: string; objectKey: string }> {
    const response = await this.axios.post('/application/file-upload', {
      sourceType,
      documentType,
      filePath,
    });
    return response.data;
  }

  async patchApplicationPullData(
    applicationId: string,
    body: Record<string, { key: string }>,
  ): Promise<Record<string, { key: string }>> {
    const response = await this.axios.patch(`/application/data/pull/${applicationId}`, body);
    return response.data;
  }
}
